import { TheWestSection } from '@news-mono/common'
import { TheWestRegionalSections } from '@west-australian-newspapers/publication-types'
import React, {
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    BreachScreenContext,
    BreachScreenType,
    click_origin_breach_screen,
    DataLayerEventName,
    SwgMetaInfo,
    useFeature,
    useSubscribeWithGoogle,
} from '@news-mono/web-common'
import { useSection, useSectionMetaInfo } from '../../../../__helpers'
import { useAuthActions } from '../../../authentication'
import { SubscriptionPriceObject } from '../../helpers/BreachWallTypes'
import {
    BreachOptions,
    PackageOptions,
    SubscriptionPackageProps,
} from '../BreachWallContent/BreachWallContent'
import { getCartPayload } from '../../../../tracking'
import {
    IconGoogle,
    StyledBenefit,
    StyledBenefitRowContainer,
    StyledBenefitsContainer,
    StyledBenefitText,
    StyledCancellationMessage,
    StyledRadioButtonContainer,
    StyledRadioRowContainer,
    StyledRadioRowText,
    StyledRibbon,
    StyledSubscribeButton,
    StyledSubscriptionButtonContainer,
    StyledSubscriptionContentContainer,
    StyledSubscriptionHeading,
    StyledSubscriptionInnerPriceContainer,
    StyledSubscriptionPackageContainer,
    StyledSubscriptionPrice,
    StyledSubscriptionPriceContainer,
    StyledSubscriptionPriceTextContainer,
    StyledSubscriptionPricingAndBenefitsContainer,
    StyledSubscriptionQualifierText,
    StyledSubscriptionRadioInput,
    StyledSubscriptionSubHeading,
    StyledSubscriptionSubPriceText,
    StyledSubscriptionTitleContainer,
    StyledSwgButton,
} from './SubscriptionPackageRedesign2026.styled'
import { createLinkClickEvent } from '../../createLinkClickHandler'
import { IconDeliveryTruck } from '../../../../icons'

const BENEFIT_TICK_ICON = require('./assets/BenefitTick.svg')
const BENEFIT_CROSS_ICON = require('./assets/BenefitCross.svg')

export const SubscriptionPackageRedesign2026: React.FC<SubscriptionPackageProps> =
    ({
        breachDeal,
        breachCreativeName,
        breachStyle,
        onEvent,
        masthead = 'The West Australian',
        packageInfo,
        packagePrices: providedPackagePrices,
        isRegionalOverride,
        desiredRedirect,
        isSwgPage = false,
    }) => {
        const {
            heading,
            subHeading,
            ribbonTitle,
            ribbonOptions,
            packageTheme = 'default',
            buttonStyle,
            benefitOverride,
            isRecommended,
        } = packageInfo

        const isRadio = packageInfo.type === 'radio-list-subscriptions'
        const packagePrices = useMemo(
            () =>
                providedPackagePrices
                    ? providedPackagePrices
                    : isRadio
                    ? packageInfo.prices
                    : [packageInfo],
            [isRadio, packageInfo, providedPackagePrices],
        )
        const defaultPackage = isRadio ? packageInfo.prices[0] : packageInfo
        const [selectedPrice, setSelectedPrice] = useState(defaultPackage)

        const benefits = useMemo(() => {
            return [
                ...(benefitOverride ?? []).map((benefit) => ({
                    benefit,
                    accessible: true,
                })),
                // Additional benefits are always accessible, false
                ...(selectedPrice.additionalBenefits ?? []).map((benefit) => ({
                    benefit,
                    accessible: false,
                })),
            ]
        }, [benefitOverride, selectedPrice])

        const {
            cancellationMessage,
            googleSKU,
            packagePath,
            offerCode,
            price,
        } = selectedPrice

        // ---- Hooks ----
        const section = useSection<TheWestSection>()
        const sectionMeta = useSectionMetaInfo()
        const { onSubscribeClick } = useAuthActions()

        // ---- Breach config options ----
        const isDigital = selectedPrice.packageType === 'digital'
        const isRegional = section in TheWestRegionalSections
        const breachName = section === 'genwest' ? 'default' : section

        const onPackageChange = useCallback(
            (pricePoint: SubscriptionPriceObject) => {
                const current = selectedPrice

                setSelectedPrice(pricePoint)
                onEvent({
                    type: DataLayerEventName.packageChange,
                    originator: 'SubscriptionPackageRedesign2026',
                    payload: {
                        from: {
                            packagePath: current.packagePath,
                            rateCode: current.rateCode,
                            price: current.price,
                        },
                        to: {
                            packagePath: pricePoint.packagePath,
                            rateCode: pricePoint.rateCode,
                            price: pricePoint.price,
                        },
                    },
                })
            },
            // onEvent can cause excessive re-renders if included in deps
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [selectedPrice],
        )

        const breachScreenContext = useContext(BreachScreenContext)
        const breachContext = breachScreenContext.context
        const breachScreenType: BreachScreenType = 'Premium - Inline'

        // ---- Subscribe with Google ----
        let swgMetaInfo: SwgMetaInfo | undefined = undefined
        const buttonRef = useRef<HTMLButtonElement>(null)
        // Matches swg-breach-albany-advertiser etc
        const isSWGBreachInterfaceEnabled = useFeature(
            `swg-breach-${breachName}`,
        )
        const subscribeWithGoogleEnabled = useFeature('subscribe-with-google')
        const showSwG =
            subscribeWithGoogleEnabled &&
            isSWGBreachInterfaceEnabled &&
            googleSKU
        if (desiredRedirect) {
            swgMetaInfo = { desiredRedirect }
        }
        useSubscribeWithGoogle(
            buttonRef.current as HTMLButtonElement,
            googleSKU ?? 'everyday_digital_4weeks',
            sectionMeta,
            breachContext,
            swgMetaInfo,
            true,
        )

        const addToCartPayload = getCartPayload(
            { price: selectedPrice, package: packageInfo },
            masthead,
            { quantity: selectedPrice.quantity },
        )

        const packageOptions: PackageOptions = {
            theme: packageTheme,
            type: selectedPrice.packageType,
            gridColumnPosition: packageInfo.columnPosition,
            isRecommended,
        }
        const breachOptions: BreachOptions = {
            style: breachStyle,
            useBreachDealStyle: isRegionalOverride,
            isRegional: isRegional,
        }

        return (
            <StyledSubscriptionPackageContainer
                breachOptions={breachOptions}
                packageOptions={packageOptions}
            >
                {ribbonOptions === 'freeDeliveryBluePill' && (
                    <StyledRibbon ribbonOption={'freeDeliveryBluePill'}>
                        <IconDeliveryTruck />
                        <span>Free delivery</span>
                    </StyledRibbon>
                )}
                <StyledSubscriptionContentContainer>
                    <StyledSubscriptionTitleContainer>
                        {subHeading && (
                            <StyledSubscriptionSubHeading>
                                {subHeading}
                            </StyledSubscriptionSubHeading>
                        )}
                        <StyledSubscriptionHeading>
                            {heading}
                        </StyledSubscriptionHeading>
                    </StyledSubscriptionTitleContainer>
                    <StyledSubscriptionPricingAndBenefitsContainer>
                        <StyledSubscriptionPriceContainer>
                            {/* An inner price container as some will have a pill */}
                            <StyledSubscriptionInnerPriceContainer>
                                <StyledSubscriptionPriceTextContainer>
                                    <StyledSubscriptionPrice
                                        packageOptions={packageOptions}
                                    >
                                        {price}
                                    </StyledSubscriptionPrice>
                                    <StyledSubscriptionSubPriceText>
                                        / week
                                    </StyledSubscriptionSubPriceText>
                                    {/* Styled only for ribbon option blue-pill atm */}
                                    {ribbonTitle &&
                                        ribbonOptions === 'bluePillLight' && (
                                            <StyledRibbon
                                                ribbonOption={'bluePillLight'}
                                            >
                                                <span>{ribbonTitle}</span>
                                            </StyledRibbon>
                                        )}
                                </StyledSubscriptionPriceTextContainer>
                            </StyledSubscriptionInnerPriceContainer>
                            {/* Price Qualifier */}
                            {selectedPrice.priceQualifier && (
                                <StyledSubscriptionQualifierText>
                                    {selectedPrice.priceQualifier}
                                </StyledSubscriptionQualifierText>
                            )}
                        </StyledSubscriptionPriceContainer>
                        <StyledBenefitsContainer>
                            {benefits.map(({ benefit, accessible }, index) => {
                                return (
                                    <StyledBenefitRowContainer key={index}>
                                        {accessible ? (
                                            <StyledBenefit
                                                alt={'Included'}
                                                src={BENEFIT_TICK_ICON}
                                            />
                                        ) : (
                                            <StyledBenefit
                                                alt={'Not included'}
                                                src={BENEFIT_CROSS_ICON}
                                            />
                                        )}
                                        <StyledBenefitText
                                            isBenefitAccessible={accessible}
                                        >
                                            {benefit}
                                        </StyledBenefitText>
                                    </StyledBenefitRowContainer>
                                )
                            })}
                        </StyledBenefitsContainer>
                    </StyledSubscriptionPricingAndBenefitsContainer>
                </StyledSubscriptionContentContainer>
                {/* If the package is a radio option */}
                {isRadio && (
                    <StyledRadioButtonContainer>
                        {packagePrices.map((pricePoint) => (
                            <StyledRadioRowContainer
                                key={pricePoint.packagePath}
                                onClick={() => onPackageChange(pricePoint)}
                                htmlFor={pricePoint.packagePath}
                            >
                                <StyledSubscriptionRadioInput
                                    type="radio"
                                    name={heading}
                                    id={pricePoint.packagePath}
                                    value={pricePoint.packagePath}
                                    checked={
                                        selectedPrice.packagePath ===
                                        pricePoint.packagePath
                                    }
                                    role={'presentation'}
                                    readOnly={true}
                                />
                                <StyledRadioRowText>
                                    {pricePoint.packageTitle}
                                </StyledRadioRowText>
                            </StyledRadioRowContainer>
                        ))}
                    </StyledRadioButtonContainer>
                )}
                {/* Subscription Buttons */}
                <StyledSubscriptionButtonContainer>
                    {!isSwgPage && (
                        <StyledSubscribeButton
                            packageOptions={packageOptions}
                            breachOptions={breachOptions}
                            buttonStyle={buttonStyle}
                            type="button"
                            title="Subscribe Button"
                            aria-label="Subscribe button"
                            onClick={() => {
                                createLinkClickEvent(
                                    breachCreativeName,
                                    breachDeal,
                                    onEvent,
                                    breachScreenType,
                                    1,
                                    addToCartPayload,
                                )()
                                onSubscribeClick({
                                    packagePath,
                                    offerCode,
                                    callToAction: click_origin_breach_screen,
                                    breachScreenType: breachScreenType,
                                })
                            }}
                        >
                            Subscribe
                        </StyledSubscribeButton>
                    )}
                    {isDigital && showSwG && (
                        <StyledSwgButton
                            ref={buttonRef}
                            type="button"
                            aria-label="Subscribe with google button"
                        >
                            <IconGoogle />
                            <span>Subscribe with Google</span>
                        </StyledSwgButton>
                    )}
                </StyledSubscriptionButtonContainer>
                {cancellationMessage && (
                    <StyledCancellationMessage>
                        {cancellationMessage}
                    </StyledCancellationMessage>
                )}
            </StyledSubscriptionPackageContainer>
        )
    }
